"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import type { SearchHit, SearchResults } from "@/lib/odds-screen/live/search";
import { matchupLabel } from "./labels";
import { metaLine } from "./time";
import { leagueLabel, marketCount } from "./view-model";

type Row = { readonly key: string; readonly hit: SearchHit; readonly primary: string; readonly secondary: string; readonly action: string };
type Group = { readonly label: string; readonly rows: readonly Row[] };

function rowFor(hit: SearchHit): Row {
  switch (hit.kind) {
    case "game": {
      const { event } = hit;
      const props = event.props > 0 ? ` · ${event.props} props` : "";
      return {
        key: `game:${hit.league}:${event.id}`,
        hit,
        primary: matchupLabel(event, hit.league, "full"),
        secondary: `${leagueLabel(hit.league)} · ${metaLine(event.startMs)}${props}`,
        action: "Open game",
      };
    }
    case "player":
      return {
        key: `player:${hit.league}:${hit.eventId}:${hit.entity.key}`,
        hit,
        primary: hit.entity.name,
        secondary: hit.event
          ? `${leagueLabel(hit.league)} · ${hit.familyLabel} · ${matchupLabel(hit.event, hit.league)}`
          : `${leagueLabel(hit.league)} · ${hit.familyLabel}`,
        action: "Compare books",
      };
    case "market": {
      const { family } = hit;
      const group = family.group ?? (family.kind === "lines" ? "Game" : "Player props");
      const count = family.kind === "props" ? marketCount(family.rows, "players") : marketCount(family.events, "games");
      return {
        key: `market:${hit.league}:${family.id}`,
        hit,
        primary: family.label,
        secondary: `${leagueLabel(hit.league)} · ${group} · ${count}`,
        action: "Open board",
      };
    }
    case "league":
      return {
        key: `league:${hit.league.id}`,
        hit,
        primary: leagueLabel(hit.league.id),
        secondary: marketCount(hit.league.events, "games"),
        action: "Switch league",
      };
  }
}

function groupsFor(results: SearchResults, empty: boolean): readonly Group[] {
  return [
    { label: empty ? "Games today" : "Games", rows: results.games.map(rowFor) },
    { label: "Players", rows: results.players.map(rowFor) },
    { label: "Markets", rows: results.markets.map(rowFor) },
    { label: "Leagues", rows: results.leagues.map(rowFor) },
  ].filter((group) => group.rows.length > 0);
}

function KindIcon({ kind }: { kind: SearchHit["kind"] }) {
  const shape = kind === "game"
    ? <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M12 5v14" /></>
    : kind === "player"
      ? <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>
      : kind === "market"
        ? <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>
        : <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></>;
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{shape}</svg>
  );
}

export function SearchPalette({
  open, query, results, isMobile, pending = false, onQuery, onPick, onClose,
}: {
  open: boolean;
  query: string;
  results: SearchResults;
  isMobile: boolean;
  pending?: boolean;
  onQuery: (next: string) => void;
  onPick: (hit: SearchHit) => void;
  onClose: () => void;
}) {
  const listId = useId();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState(0);
  const empty = query.trim() === "";
  const groups = useMemo(() => groupsFor(results, empty), [results, empty]);
  const flat = useMemo(() => groups.flatMap((group) => group.rows), [groups]);

  useEffect(() => { setActive(0); }, [results]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") { event.preventDefault(); onClose(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>("[data-active]")?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!open || typeof document === "undefined") return null;

  const onInputKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") { event.preventDefault(); setActive((i) => Math.min(i + 1, flat.length - 1)); }
    else if (event.key === "ArrowUp") { event.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (event.key === "Enter") { const chosen = flat[active]; if (chosen) { event.preventDefault(); onPick(chosen.hit); } }
  };

  let offset = 0;
  const list = groups.map((group) => {
    const start = offset;
    offset += group.rows.length;
    return (
      <div key={group.label} role="group" aria-label={group.label}>
        <span className="block px-2.5 pb-1 pt-2 text-[10px] font-medium uppercase tracking-[0.08em] text-zinc-500">{group.label}</span>
        {group.rows.map((row, i) => {
          const index = start + i;
          const highlighted = index === active;
          return (
            <button
              key={row.key}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={highlighted}
              data-active={highlighted || undefined}
              onMouseDown={(event) => event.preventDefault()}
              onMouseEnter={() => setActive(index)}
              onClick={() => onPick(row.hit)}
              className={cn(
                "grid w-full grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-2.5 rounded-md px-2.5 text-left",
                isMobile ? "h-12" : "h-10",
                highlighted ? "bg-white/[0.06] text-foreground" : "text-zinc-300 hover:bg-white/[0.04]",
              )}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-white/[0.05] text-zinc-400"><KindIcon kind={row.hit.kind} /></span>
              <span className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-medium">{row.primary}</span>
                <span className="truncate text-[11px] text-zinc-500">{row.secondary}</span>
              </span>
              <span className="whitespace-nowrap text-[11px] text-zinc-500">{row.action}</span>
            </button>
          );
        })}
      </div>
    );
  });

  const panel = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      className={cn(
        "pointer-events-auto flex flex-col bg-surface-overlay",
        isMobile
          ? "absolute inset-0 pb-[max(1rem,env(safe-area-inset-bottom))]"
          : "w-[600px] max-w-[calc(100vw-24px)] rounded-xl border border-surface-line shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] animate-in fade-in-0 zoom-in-95 duration-150 motion-reduce:animate-none",
      )}
    >
      <div className={cn("flex flex-shrink-0 items-center gap-3 border-b border-surface-line px-4", isMobile ? "h-14" : "h-12")}>
        <span className="flex text-zinc-500">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
        </span>
        <input
          type="search"
          value={query}
          autoFocus
          onChange={(event) => onQuery(event.target.value)}
          onKeyDown={onInputKey}
          placeholder="Search games, players, markets, leagues"
          aria-label="Search"
          role="combobox"
          aria-expanded={flat.length > 0}
          aria-autocomplete="list"
          aria-controls={flat.length > 0 ? listId : undefined}
          aria-activedescendant={flat.length > 0 ? `${listId}-${active}` : undefined}
          className={cn(
            "min-w-0 flex-grow bg-transparent text-foreground outline-none placeholder:text-zinc-500 [&::-webkit-search-cancel-button]:hidden",
            isMobile ? "text-[16px]" : "text-[15px]",
          )}
        />
        {isMobile ? (
          <button type="button" onClick={onClose} className="flex h-9 flex-shrink-0 items-center text-[14px] font-medium text-zinc-300">Cancel</button>
        ) : (
          <kbd className="flex h-[18px] flex-shrink-0 items-center rounded border border-surface-line px-1.5 font-sans text-[10px] font-medium text-zinc-500">esc</kbd>
        )}
      </div>
      {flat.length > 0 ? (
        <div ref={listRef} id={listId} role="listbox" aria-label="Search results" className={cn("overflow-y-auto p-1.5", isMobile ? "min-h-0 flex-grow" : "max-h-[min(60vh,480px)]")}>
          {list}
        </div>
      ) : empty ? null : pending ? (
        <div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
          <span className="text-[13px] text-zinc-500">Searching…</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-1 px-6 py-10 text-center">
          <span className="text-[14px] font-semibold text-foreground">No matches for “{query.trim()}”</span>
          <span className="text-[12px] text-zinc-500">Try a team, a player, a market like “receptions”, or a league.</span>
        </div>
      )}
      {!isMobile && (
        <div className="flex h-9 flex-shrink-0 items-center justify-between border-t border-surface-line px-4 text-[11px] text-zinc-500">
          <span className="flex items-center gap-3">
            <span><span className="text-zinc-400">↑↓</span> move</span>
            <span><span className="text-zinc-400">↵</span> open</span>
          </span>
          {empty ? <span>Games and players across every league</span> : pending && <span>Searching…</span>}
        </div>
      )}
    </div>
  );

  return createPortal(
    <div className="odds-black fixed inset-0 z-[60]">
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 cursor-default bg-[rgba(8,9,10,0.62)]" />
      {isMobile ? panel : <div className="pointer-events-none absolute inset-x-0 top-14 flex justify-center">{panel}</div>}
    </div>,
    document.body,
  );
}
